import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import axios from "axios";
import { toast } from "sonner";
import { Music2, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${BACKEND_URL}/api`;

/**
 * Settings > Genre. Pick one lane so you compete on the right leaderboard.
 * Hip-hop DJs vs hip-hop DJs, not vs amapiano. Displayed as coloured pills
 * matching the leaderboard colour scheme so the mental model is consistent.
 */
export default function GenrePickerCard() {
  const { user, refreshUser } = useAuth();
  const [genres, setGenres] = useState([]);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(user?.genre || null);

  useEffect(() => {
    axios.get(`${API}/leaderboard/genres`)
      .then(r => setGenres((r.data.genres || []).filter(g => g.id !== "all")))
      .catch(() => {});
  }, []);
  useEffect(() => { setCurrent(user?.genre || null); }, [user?.genre]);

  const pick = async (id) => {
    if (saving || id === current) return;
    setSaving(true);
    try {
      await axios.post(`${API}/me/genre`, { genre: id });
      setCurrent(id);
      toast.success("Genre updated — you now compete on that leaderboard.");
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save genre");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-6 space-y-4" data-testid="genre-picker-card">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#FF2A6D]/10 border border-[#FF2A6D] flex items-center justify-center"><Music2 className="w-5 h-5 text-[#FF2A6D]"/></div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF2A6D]">// Your lane</div>
          <div className="font-display font-black text-2xl">Pick your genre</div>
        </div>
        {saving && <Loader2 className="w-4 h-4 animate-spin text-[#FF2A6D]"/>}
      </div>
      <p className="text-sm text-[#94A3B8]">
        The DJ leaderboard is split by genre so you're only ranked against DJs playing what you play. Change any time.
      </p>
      <div className="flex flex-wrap gap-2" data-testid="genre-options">
        {genres.map(g => {
          const active = current === g.id;
          return (
            <button key={g.id} data-testid={`genre-pick-${g.id}`}
              onClick={() => pick(g.id)} disabled={saving}
              className={`px-3 py-2 rounded-full text-xs font-black uppercase tracking-widest border transition-colors flex items-center gap-1.5 ${active ? "text-black" : "text-[#94A3B8] hover:text-white"}`}
              style={active
                ? { background: g.color, borderColor: g.color }
                : { borderColor: "#2A354B" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: g.color }}/>
              {g.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
